import React, { useState } from 'react';
import { Card, Row, Col, Tag, Button, Modal, Alert, Divider, Tabs } from 'antd';
import { motion } from 'framer-motion';
import { 
  ThunderboltOutlined,
  DollarOutlined,
  CustomerServiceOutlined,
  ClockCircleOutlined,
  InboxOutlined,
  ToolOutlined,
  ApiOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';

const { TabPane } = Tabs;

interface PremiumService {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  color: string;
  features: string[];
  advantages: string[];
  compareWith: {
    us: string;
    competitor: string;
    advantage: string;
  }[];
  price?: string;
  timeLimit?: string;
}

const PremiumServices: React.FC = () => {
  const [selectedService, setSelectedService] = useState<PremiumService | null>(null);
  const [modalVisible, setModalVisible] = useState(false);

  const premiumServices: PremiumService[] = [
    {
      id: 'express',
      title: '闪电加急服务',
      subtitle: 'Lightning Express Service',
      icon: <ThunderboltOutlined />,
      color: '#ff4d4f',
      features: [
        '紧急货物优先处理',
        '最近航班安排',
        '机场到机场48小时',
        '专属客服跟进',
        '实时状态推送'
      ],
      advantages: [
        '比同行快2-3天',
        '专属绿色通道',
        '24小时不间断服务',
        '失败全额退款'
      ],
      compareWith: [
        {
          us: '紧急件48小时起飞',
          competitor: '一般3-5天安排航班',
          advantage: '时效提升60%'
        },
        {
          us: '专属客服1对1跟进',
          competitor: '共享客服资源',
          advantage: '服务响应更快'
        },
        {
          us: '失败全额退款保障',
          competitor: '无特殊保障',
          advantage: '零风险承诺'
        }
      ],
      price: '+5-8元/kg',
      timeLimit: '48小时内起飞'
    },
    {
      id: 'transparent',
      title: '透明价格体系',
      subtitle: 'Transparent Pricing System',
      icon: <DollarOutlined />,
      color: '#52c41a',
      features: [
        '明码标价无隐藏费用',
        '在线实时报价系统',
        '详细费用分解说明',
        '价格锁定7天有效',
        '多重优惠自动计算'
      ],
      advantages: [
        '价格透明度100%',
        '无任何隐藏收费',
        '优惠政策自动应用',
        '价格争议零投诉'
      ],
      compareWith: [
        {
          us: '所有费用明细公开',
          competitor: '隐藏附加费用',
          advantage: '成本可控性强'
        },
        {
          us: '7天价格锁定保障',
          competitor: '价格随时波动',
          advantage: '预算规划便利'
        },
        {
          us: '自动优惠计算',
          competitor: '需要主动申请',
          advantage: '省心省力'
        }
      ]
    },
    {
      id: 'multimodal',
      title: '多样化货物处理',
      subtitle: 'Diversified Cargo Handling',
      icon: <InboxOutlined />,
      color: '#1890ff',
      features: [
        '普货标准化流程',
        '带电产品专业认证',
        '超大件定制方案',
        '危险品合规处理',
        '生鲜冷链服务'
      ],
      advantages: [
        '货物类型覆盖率95%',
        '专业认证齐全',
        '定制化解决方案',
        '合规性100%保证'
      ],
      compareWith: [
        {
          us: '支持5大类特殊货物',
          competitor: '仅支持普货',
          advantage: '服务范围广'
        },
        {
          us: '专业认证证书齐全',
          competitor: '认证不完整',
          advantage: '合规风险低'
        },
        {
          us: '定制化处理方案',
          competitor: '标准化流程',
          advantage: '灵活性更强'
        }
      ]
    },
    {
      id: 'technology',
      title: '智能物流系统',
      subtitle: 'Smart Logistics Platform',
      icon: <ApiOutlined />,
      color: '#722ed1',
      features: [
        '自主研发ERP系统',
        'API接口全链条对接',
        'AI智能路径规划',
        '实时数据可视化',
        '预警风险管控'
      ],
      advantages: [
        '系统响应速度快',
        'API对接成功率99%',
        '数据准确性高',
        '操作效率提升30%'
      ],
      compareWith: [
        {
          us: '自主研发核心系统',
          competitor: '使用第三方系统',
          advantage: '功能更匹配'
        },
        {
          us: 'API全链条对接',
          competitor: '部分环节断链',
          advantage: '信息更透明'
        },
        {
          us: 'AI智能优化配载',
          competitor: '人工经验配载',
          advantage: '成本节省15%'
        }
      ]
    },
    {
      id: 'valueadded',
      title: '增值服务套餐',
      subtitle: 'Value-Added Services',
      icon: <ToolOutlined />,
      color: '#fa8c16',
      features: [
        '免费仓储7天',
        '代检代查服务',
        '小件重新包装',
        '标签重贴换标',
        '质检报告代办'
      ],
      advantages: [
        '服务项目最全面',
        '专业操作团队',
        '质量标准严格',
        '效率行业领先'
      ],
      compareWith: [
        {
          us: '免费仓储7天',
          competitor: '仅提供3天',
          advantage: '时间更充裕'
        },
        {
          us: '专业质检团队',
          competitor: '外包质检',
          advantage: '标准更统一'
        },
        {
          us: '8项增值服务',
          competitor: '3-4项基础服务',
          advantage: '选择更丰富'
        }
      ]
    },
    {
      id: 'premium',
      title: '白金客户专享',
      subtitle: 'Platinum Customer Exclusive',
      icon: <CustomerServiceOutlined />,
      color: '#faad14',
      features: [
        '专属客户经理',
        '优先舱位保障',
        'VIP快速通道',
        '定制化解决方案',
        '专属优惠政策'
      ],
      advantages: [
        '1对1专属服务',
        '舱位保障率100%',
        '处理速度快50%',
        '享受最优价格'
      ],
      compareWith: [
        {
          us: '专属客户经理服务',
          competitor: '共享客服资源',
          advantage: '服务连续性强'
        },
        {
          us: '舱位优先保障',
          competitor: '公平排队原则',
          advantage: '旺季无忧'
        },
        {
          us: '定制优惠政策',
          competitor: '标准价格',
          advantage: '成本更优'
        }
      ]
    }
  ];

  const handleServiceClick = (service: PremiumService) => {
    setSelectedService(service);
    setModalVisible(true);
  };

  const ServiceComparisonTable = ({ service }: { service: PremiumService }) => (
    <div style={{ marginTop: '24px' }}>
      <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
        🏆 对比同行优势
      </h4>
      {service.compareWith.map((compare, index) => (
        <div key={index} style={{ 
          marginBottom: '16px',
          padding: '16px',
          background: '#fafafa',
          borderRadius: '8px',
          borderLeft: `4px solid ${service.color}`
        }}>
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={8}>
              <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                📈 我们的标准
              </div>
              <div style={{ fontWeight: '600', color: service.color, fontSize: '14px' }}>
                {compare.us}
              </div>
            </Col>
            <Col xs={24} sm={8}>
              <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                📊 同行标准
              </div>
              <div style={{ color: '#999', fontSize: '14px' }}>
                {compare.competitor}
              </div>
            </Col>
            <Col xs={24} sm={8}>
              <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                💪 核心优势
              </div>
              <div style={{ fontWeight: '600', color: '#52c41a', fontSize: '14px' }}>
                {compare.advantage}
              </div>
            </Col>
          </Row>
        </div>
      ))}
    </div>
  );

  return (
    <section style={{ 
      padding: '80px 0', 
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          style={{ textAlign: 'center', marginBottom: '60px' }}
        >
          <h2 style={{ 
            fontSize: '2.5rem',
            fontWeight: '600',
            marginBottom: '24px',
            color: '#1d1d1f',
            fontFamily: "'Inter Tight', Inter, sans-serif",
          }}>
            专业增值服务
          </h2>
          <p style={{ 
            color: '#64748b', 
            maxWidth: '800px', 
            margin: '0 auto',
            fontSize: '1.125rem'
          }}>
            参考DHL、FedEx等顶级物流公司标准，提供超越同行的专业化服务体系
          </p>
        </motion.div>

        {/* 服务对比概览 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          style={{ marginBottom: '60px' }}
        >
          <Card
            style={{
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(20px)',
              border: 'none',
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
            }}
          >
            <Alert
              message="🎯 服务对比优势"
              description="对标国际顶级物流公司服务标准，全面超越同行专线水平"
              type="info"
              showIcon
              style={{ marginBottom: '24px' }}
            />
            
            <Row gutter={[24, 16]}>
              <Col xs={24} sm={12} md={6}>
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#52c41a', marginBottom: '8px' }}>
                    6项
                  </div>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    专业增值服务
                  </div>
                  <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                    vs 同行2-3项
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#1890ff', marginBottom: '8px' }}>
                    48h
                  </div>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    加急服务起飞
                  </div>
                  <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                    vs 同行3-5天
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#722ed1', marginBottom: '8px' }}>
                    100%
                  </div>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    价格透明度
                  </div>
                  <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                    vs 同行隐藏收费
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#fa8c16', marginBottom: '8px' }}>
                    95%
                  </div>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    货物类型覆盖
                  </div>
                  <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                    vs 同行60-70%
                  </div>
                </div>
              </Col>
            </Row>
          </Card>
        </motion.div>

        {/* 增值服务卡片 */}
        <Row gutter={[24, 24]}>
          {premiumServices.map((service, index) => (
            <Col xs={24} sm={12} lg={8} key={service.id}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card
                  hoverable
                  onClick={() => handleServiceClick(service)}
                  style={{
                    height: '100%',
                    borderRadius: '16px',
                    border: `2px solid ${service.color}20`,
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  bodyStyle={{ padding: '24px' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-8px)';
                    e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.08)';
                  }}
                >
                  <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <div style={{
                      width: '64px',
                      height: '64px',
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${service.color}20, ${service.color}40)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px',
                      fontSize: '28px',
                      color: service.color
                    }}>
                      {service.icon}
                    </div>
                    
                    <h3 style={{ 
                      margin: '0 0 8px 0',
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#1D1D1F'
                    }}>
                      {service.title}
                    </h3>
                    
                    <p style={{ 
                      margin: '0 0 16px 0',
                      fontSize: '13px',
                      color: '#666',
                      fontStyle: 'italic'
                    }}>
                      {service.subtitle}
                    </p>
                    
                    {(service.price || service.timeLimit) && (
                      <div style={{ marginBottom: '16px' }}>
                        {service.price && (
                          <Tag color={service.color} style={{ marginBottom: '4px' }}>
                            {service.price}
                          </Tag>
                        )}
                        {service.timeLimit && (
                          <Tag color="blue">
                            <ClockCircleOutlined style={{ marginRight: '4px' }} />
                            {service.timeLimit}
                          </Tag>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ 
                      fontSize: '14px', 
                      fontWeight: '600', 
                      marginBottom: '12px',
                      color: service.color 
                    }}>
                      🎯 核心特色
                    </h4>
                    <ul style={{ 
                      paddingLeft: '16px', 
                      margin: 0, 
                      fontSize: '13px',
                      color: '#666'
                    }}>
                      {service.features.slice(0, 3).map((feature, idx) => (
                        <li key={idx} style={{ marginBottom: '6px', lineHeight: '1.4' }}>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div style={{ textAlign: 'center' }}>
                    <Button 
                      type="primary" 
                      size="small"
                      style={{ 
                        background: service.color,
                        borderColor: service.color,
                        borderRadius: '20px',
                        fontSize: '12px'
                      }}
                    >
                      查看详情
                    </Button>
                  </div>
                </Card>
              </motion.div>
            </Col>
          ))}
        </Row>

        {/* 服务详情模态框 */}
        <Modal
          title={
            selectedService ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ 
                  fontSize: '24px', 
                  color: selectedService.color 
                }}>
                  {selectedService.icon}
                </div>
                <div>
                  <div style={{ fontSize: '18px', fontWeight: '600' }}>
                    {selectedService.title}
                  </div>
                  <div style={{ fontSize: '13px', color: '#666', fontStyle: 'italic' }}>
                    {selectedService.subtitle}
                  </div>
                </div>
              </div>
            ) : null
          }
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setModalVisible(false)}>
              关闭
            </Button>,
            <Button key="contact" type="primary">
              立即咨询
            </Button>
          ]}
          width={800}
          style={{ maxWidth: '95vw' }}
        >
          {selectedService && (
            <Tabs defaultActiveKey="features">
              <TabPane tab="服务特色" key="features">
                <div style={{ padding: '16px 0' }}>
                  <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
                    🎯 服务特色
                  </h4>
                  <Row gutter={[16, 12]}>
                    {selectedService.features.map((feature, index) => (
                      <Col xs={24} sm={12} key={index}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <CheckCircleOutlined style={{ color: selectedService.color, fontSize: '16px' }} />
                          <span style={{ fontSize: '14px' }}>{feature}</span>
                        </div>
                      </Col>
                    ))}
                  </Row>
                  
                  <Divider />
                  
                  <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
                    💪 核心优势
                  </h4>
                  <ul style={{ paddingLeft: '20px', fontSize: '14px' }}>
                    {selectedService.advantages.map((advantage, index) => (
                      <li key={index} style={{ marginBottom: '8px', lineHeight: '1.5' }}>
                        {advantage}
                      </li>
                    ))}
                  </ul>
                </div>
              </TabPane>
              
              <TabPane tab="对比优势" key="comparison">
                <ServiceComparisonTable service={selectedService} />
              </TabPane>
            </Tabs>
          )}
        </Modal>
      </div>
    </section>
  );
};

export default PremiumServices;

